import { createApp } from 'vue'

// Commons PrimeVue components
import PrimeVue from 'primevue/config'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import ConfirmationService from 'primevue/confirmationservice'
import ConfirmDialog from 'primevue/confirmdialog'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import HelpMessage from '@/components/HelpMessage.vue'

import App from './App.vue'
import router from '@/services/router'
import db from '@/services/db-plugin'
import { writeLastSessionRoute } from '@/services/session-nav-storage'
import supabase from '@/services/supabase'
import { utils } from '@/services/utils'
import { debounceDirective } from './services/debounce'

import 'bootstrap/dist/css/bootstrap-utilities.css'
import 'primeicons/primeicons.css'
import 'primevue/resources/themes/lara-light-indigo/theme.css'
import 'primevue/resources/primevue.min.css'
import './main.scss'

utils()

const hash = window.location.hash
if (hash.startsWith('#/')) {
  // Legacy hash routes → history paths. Do not touch Supabase auth fragments.
  // hash.slice(1) is '/sessions/...'; avoid joining with pathname '/' → '//'
  window.location.replace(hash.slice(1))
} else {
  let cachedUser = null
  let authResolved = false

  const authStateReady = supabase.auth.getSession().then(({ data }) => {
    cachedUser = data.session?.user ?? null
    authResolved = true
    return cachedUser
  })

  const app = createApp(App)
    .use(router)
    .use(db)
    .use(PrimeVue)
    .use(ConfirmationService)
    .use(ToastService)
    .directive('tooltip', Tooltip)
    .directive('debounce', debounceDirective)
    .component('Dialog', Dialog)
    .component('Button', Button)
    .component('DataTable', DataTable)
    .component('Column', Column)
    .component('InputText', InputText)
    .component('ConfirmDialog', ConfirmDialog)
    .component('Dropdown', Dropdown)
    .component('HelpMessage', HelpMessage)

  const publicRouteNames = ['login', 'register', 'reset-password', 'update-password']

  supabase.auth.onAuthStateChange((event, session) => {
    cachedUser = session?.user ?? null
    authResolved = true
    if (app._instance) app._instance.data.user = cachedUser
    if (event === 'PASSWORD_RECOVERY' && router.currentRoute.value.name !== 'update-password') {
      router.replace({ name: 'update-password' })
    }
    if (event === 'SIGNED_OUT') {
      const current = router.currentRoute.value
      const isPublicInventory = current.name === 'inventories_public'
        || (current.name === 'session_inventory' && current.query.public === 'true')
      if (!publicRouteNames.includes(current.name) && !isPublicInventory) {
        router.replace({ name: 'login' })
      }
    }
  })

  router.beforeEach(async (to) => {
    if (!authResolved) await authStateReady
    const user = cachedUser ?? app._instance?.data?.user ?? null
    const isPublicInventory = to.name === 'inventories_public'
      || (to.name === 'session_inventory' && to.query.public === 'true')
    if (!user && !publicRouteNames.includes(to.name) && !isPublicInventory) return { name: 'login' }
  })

  router.afterEach((to) => {
    writeLastSessionRoute(to.name)
  })

  // On mobile/desktop, browser tabs can be suspended for hours. When the tab
  // becomes visible again after 10+ hours, verify auth and refresh all data.
  let hiddenAt = null
  let rootProxy = null
  const TEN_HOURS = 10 * 60 * 60 * 1000

  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      return
    }

    // visible — only act if hidden for 10+ hours
    if (!hiddenAt || Date.now() - hiddenAt < TEN_HOURS) return
    hiddenAt = null

    const { data } = await supabase.auth.getSession()
    cachedUser = data.session?.user ?? null
    if (rootProxy) rootProxy.user = cachedUser

    if (!cachedUser) {
      const current = router.currentRoute.value
      const isPublicInventory = current.name === 'inventories_public'
        || (current.name === 'session_inventory' && current.query.public === 'true')
      if (!publicRouteNames.includes(current.name) && !isPublicInventory) {
        router.replace({ name: 'login' })
      }
      return
    }

    if (!rootProxy) return

    if (rootProxy.hasUnsavedChanges) {
      rootProxy.$confirm.require({
        message: 'Data is outdated (tab was inactive for 10+ hours). Refresh now? Unsaved changes will be lost.',
        header: 'Refresh data?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Refresh',
        rejectLabel: 'Keep editing',
        accept: () => rootProxy.refreshData(),
      })
    } else {
      rootProxy.refreshData()
    }
  })

  app.config.unwrapInjectedRef = true
  app.mount('#app')
  // Store the root component proxy after mount — avoids relying on the
  // internal app._instance API which is undocumented and may break.
  rootProxy = app._instance.proxy
}
